import React, { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Table, Select, Collapse, Alert, DatePicker, Space, Empty, Button, message, InputNumber, Input } from "antd";
import moment from "moment";
import ENVIRONMENT from "../../../../environments";
import axios from "axios";
import { LoadingOutlined, WarningOutlined, ExperimentOutlined, CalendarOutlined, DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import { exportToCSV, generateExportFilename } from "../../Utilities";
import { withRouter } from "react-router-dom";
import "./ModelsWarning.scss";

const { Panel } = Collapse;
const { RangePicker } = DatePicker;

// Constants
const DATE_FORMAT = "YYYY-MM-DD";
const DEFAULT_THRESHOLD = 300;
const DEFAULT_NUMBER_OF_FIXES_THRESHOLD = 3;

// Date range presets
const DATE_PRESETS = {
  current_month: {
    label: "Current Month",
    getDates: () => ({
      start: moment().startOf("month").format(DATE_FORMAT),
      end: moment().format(DATE_FORMAT)
    })
  },
  last_month: {
    label: "Last Month",
    getDates: () => ({
      start: moment().subtract(1, 'months').startOf("month").format(DATE_FORMAT),
      end: moment().subtract(1, 'months').endOf("month").format(DATE_FORMAT)
    })
  },
  last_quarter: {
    label: "Last Quarter",
    getDates: () => ({
      start: moment().subtract(1, 'quarter').startOf("quarter").format(DATE_FORMAT),
      end: moment().subtract(1, 'quarter').endOf("quarter").format(DATE_FORMAT)
    })
  },
  custom: {
    label: "Custom",
    getDates: () => ({ start: null, end: null })
  }
};

const ModelsWarning = ({ 
  startDate = moment().startOf("month").format(DATE_FORMAT), 
  endDate = moment().format(DATE_FORMAT),
  type = "high_cost", // "high_cost" or "get_customers_test_models"
  title = "High Cost Models",
  icon = <WarningOutlined style={{ marginRight: 8, fontSize: '20px' }} />,
  borderColor = '#ff4d4f',
  backgroundColor = 'linear-gradient(135deg, #fff5f5 0%, #fff 100%)',
  shadowColor = 'rgba(255, 77, 79, 0.15)',
  companies = [], // Array of companies with {label, value} format
  modelStatus,
  history,
  location
}) => {
  // Component prefix for URL parameters
  const getComponentPrefix = () => type === "high_cost" ? "hw" : "tw";

  // State
  const [models, setModels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasModels, setHasModels] = useState(false);
  const [error, setError] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [activeCollapseKeys, setActiveCollapseKeys] = useState([]);
  
  // Filter state
  const [selectedDateRange, setSelectedDateRange] = useState('current_month');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState(startDate);
  const [filterEndDate, setFilterEndDate] = useState(endDate);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [highCostThreshold, setHighCostThreshold] = useState(DEFAULT_THRESHOLD);
  const [numberOfFixesThreshold, setNumberOfFixesThreshold] = useState(DEFAULT_NUMBER_OF_FIXES_THRESHOLD);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Table search state
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = React.useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0] || "");
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${title}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              if (clearFilters) handleReset(clearFilters);
              confirm({ closeDropdown: true });
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      const target = record[dataIndex];
      if (target === undefined || target === null) return false;
      return target.toString().toLowerCase().includes(value.toLowerCase());
    },
  });

  // Default companies list
  const companiesList = companies?.length > 0 ? companies : [{ label: "All Companies", value: "all" }];

  // Check if filters are applied (non-default values)
  const areFiltersApplied = useCallback(() => {
    if (selectedDateRange !== 'current_month') return true;

    if (selectedCompany !== 'all') return true;

    if (type === "high_cost" && highCostThreshold !== DEFAULT_THRESHOLD) return true;

    // Check custom dates
    if (selectedDateRange === 'custom' && (customStartDate || customEndDate)) return true;

    // Check pagination (not default page 1 or page size 10)
    if (currentPage !== 1 || pageSize !== 10) return true;

    return false;
  }, [selectedDateRange, selectedCompany, highCostThreshold, type, customStartDate, customEndDate, currentPage, pageSize]);

  // Date options for dropdown
  const dateOptions = Object.entries(DATE_PRESETS).map(([value, preset]) => ({
    label: preset.label,
    value
  }));

  // URL parameter parsing
  const parseURLParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const prefix = getComponentPrefix();
    
    const hasCustomDates = searchParams.has(`${prefix}_sd`) && searchParams.has(`${prefix}_ed`);
    const hasPresetRange = searchParams.has(`${prefix}_dr`);
    
    let selectedDateRange;
    if (hasCustomDates) {
      selectedDateRange = 'custom';
    } else if (hasPresetRange) {
      selectedDateRange = searchParams.get(`${prefix}_dr`);
    } else {
      selectedDateRange = 'current_month';
    }
    
    return {
      selectedDateRange,
      selectedCompany: searchParams.get(`${prefix}_cp`) || 'all',
      highCostThreshold: searchParams.get(`${prefix}_t`) ? parseInt(searchParams.get(`${prefix}_t`)) : DEFAULT_THRESHOLD,
      customStartDate: searchParams.get(`${prefix}_sd`) ? moment(searchParams.get(`${prefix}_sd`)) : null,
      customEndDate: searchParams.get(`${prefix}_ed`) ? moment(searchParams.get(`${prefix}_ed`)) : null,
      currentPage: searchParams.get(`${prefix}_page`) ? parseInt(searchParams.get(`${prefix}_page`), 10) : 1,
      pageSize: searchParams.get(`${prefix}_pageSize`) ? parseInt(searchParams.get(`${prefix}_pageSize`), 10) : 10
    };
  }, [location.search]);

  // URL updating
  const updateURL = useCallback((filters) => {
    const searchParams = new URLSearchParams(location.search);
    const prefix = getComponentPrefix();
    
    // Update only provided parameters, preserve existing ones, check to handle custom and preset date ranges in query params
    if (filters.selectedDateRange !== undefined) {
      if (filters.selectedDateRange === 'custom') {
        searchParams.delete(`${prefix}_dr`);
      } else {
        searchParams.delete(`${prefix}_sd`);
        searchParams.delete(`${prefix}_ed`);
        searchParams.set(`${prefix}_dr`, filters.selectedDateRange);
      }
    }
    
    if (filters.selectedCompany !== undefined) searchParams.set(`${prefix}_cp`, filters.selectedCompany);
    if (filters.highCostThreshold !== undefined) {
      if (filters.highCostThreshold === DEFAULT_THRESHOLD) {
        searchParams.delete(`${prefix}_t`);
      } else {
        searchParams.set(`${prefix}_t`, filters.highCostThreshold);
      }
    }
    
    if (filters.customStartDate !== undefined) {
      if (filters.customStartDate) {
        searchParams.set(`${prefix}_sd`, filters.customStartDate);
      } else {
        searchParams.delete(`${prefix}_sd`);
      }
    }
    
    if (filters.customEndDate !== undefined) {
      if (filters.customEndDate) {
        searchParams.set(`${prefix}_ed`, filters.customEndDate);
      } else {
        searchParams.delete(`${prefix}_ed`);
      }
    }
    
    // Handle pagination
    if (filters.currentPage !== undefined) {
      if (filters.currentPage && filters.currentPage > 1) {
        searchParams.set(`${prefix}_page`, filters.currentPage.toString());
      } else {
        searchParams.delete(`${prefix}_page`);
      }
    }
    
    if (filters.pageSize !== undefined) {
      if (filters.pageSize && filters.pageSize !== 10) {
        searchParams.set(`${prefix}_pageSize`, filters.pageSize.toString());
      } else {
        searchParams.delete(`${prefix}_pageSize`);
      }
    }

    const newURL = `${location.pathname}?${searchParams.toString()}`;
    history.replace(newURL);
  }, [location.search, location.pathname, history]);

  // Handle pagination change
  const handleTableChange = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    
    // Update URL with new pagination state
    updateURL({
      currentPage: pagination.current,
      pageSize: pagination.pageSize
    });
  };

  // Date range change handler
  const handleDateRangeChange = useCallback((value) => {
    if (value === 'custom') {
      setSelectedDateRange(value);
      return;
    }
    
    const preset = DATE_PRESETS[value];
    if (preset) {
      const { start, end } = preset.getDates();
      setSelectedDateRange(value);
      setFilterStartDate(start);
      setFilterEndDate(end);
      setCustomStartDate(null);
      setCustomEndDate(null);
      
      // Reset pagination to page 1 when filters change
      setCurrentPage(1);
      
      updateURL({
        selectedDateRange: value,
        customStartDate: null,
        customEndDate: null,
        currentPage: 1
      });
    }
  }, [updateURL]);

  // Custom date change handler
  const handleCustomDateChange = useCallback((dates) => {
    if (dates?.length === 2) {
      const [start, end] = dates;
      
      if (start.isAfter(end)) return;
      
      setCustomStartDate(start);
      setCustomEndDate(end);
      setFilterStartDate(start.format(DATE_FORMAT));
      setFilterEndDate(end.format(DATE_FORMAT));
      
      // Reset pagination to page 1 when filters change
      setCurrentPage(1);
      
      updateURL({
        selectedDateRange: 'custom',
        customStartDate: start.format(DATE_FORMAT),
        customEndDate: end.format(DATE_FORMAT),
        currentPage: 1
      });
    } else {
      setCustomStartDate(null);
      setCustomEndDate(null);
      
      // Reset pagination to page 1 when filters change
      setCurrentPage(1);
      
      updateURL({
        selectedDateRange: 'custom',
        customStartDate: null,
        customEndDate: null,
        currentPage: 1
      });
    }
  }, [updateURL]);

  // Company change handler
  const handleCompanyChange = useCallback((value) => {
    setSelectedCompany(value);
    
    // Reset pagination to page 1 when filters change
    setCurrentPage(1);
    
    updateURL({ 
      selectedCompany: value,
      currentPage: 1
    });
  }, [updateURL]);

  // Threshold change handler
  const handleHighCostThresholdChange = (value) => {
    if (value !== null && value > 0) {
      // Clear any existing timeout, to avoid multiple API calls.
      if (window.highCostThresholdTimeout) {
        clearTimeout(window.highCostThresholdTimeout);
      }

      window.highCostThresholdTimeout = setTimeout(() => {
        setHighCostThreshold(value);
        
        // Reset pagination to page 1 when filters change
        setCurrentPage(1);
        
        updateURL({ 
          highCostThreshold: value,
          currentPage: 1
        });
      }, 500);
    }
  };

  const handleNumberOfFixesThresholdChange = (value) => {
    if (value !== null && value > 0) {
      setNumberOfFixesThreshold(value);
      setCurrentPage(1);
    }
  };

  // Export handler
  const handleExport = useCallback(() => {
    if (!models?.length) {
      message.warning('No data to export');
      return;
    }

    const filename = generateExportFilename(title.toLowerCase().replace(/\s+/g, '-'), filterStartDate, filterEndDate);
    
    const exportColumns = [
      { title: 'Model ID', dataIndex: 'id' },
      { title: 'Model Name', dataIndex: 'name' },
      { title: 'Username', dataIndex: 'customer_username' },
      { title: 'Company', dataIndex: 'company' },
      { title: 'Category', dataIndex: 'category' },
      { title: 'Artist Cost', dataIndex: 'artist_modeling_cost' },
      { title: 'Automation Cost', dataIndex: 'automation_cost' },
      { title: 'Total Cost', dataIndex: 'total_cost' },
    ];

    const valueFormatter = (value, row, col) => {
      const currencyFields = ['artist_modeling_cost', 'automation_cost', 'total_cost'];
      return currencyFields.includes(col.dataIndex) ? `$${value || 0}` : value;
    };

    exportToCSV(filename, exportColumns, models, { valueFormatter });
  }, [models, title, filterStartDate, filterEndDate]);

  // Data fetching
  const getModels = useCallback(() => {
    const payload = {
      action: type === "high_cost" ? "get_high_cost_models" : "get_customers_test_models",
      start_date: filterStartDate,
      end_date: filterEndDate,
      ...(selectedCompany !== 'all' && { company_id: selectedCompany }),
      ...(type === "high_cost" && { high_cost_threshold: highCostThreshold }),
      ...(modelStatus && { is_approved: modelStatus == 'approved' }),
      ...(type === "high_cost" && { number_of_fixes_threshold: numberOfFixesThreshold })
    };
    
    setIsLoading(true);
    setError(false);
    
    axios.post(ENVIRONMENT.GET_MODELING_COST_REPORT, payload)
      .then((response) => {
        const responseData = response.data || [];
        setModels(responseData);
        setHasModels(responseData.length > 0);
        setError(false);
      })
      .catch((error) => {
        console.error(`Error fetching ${title}:`, error);
        setModels([]);
        setHasModels(false);
        setError(true);
      })
      .finally(() => setIsLoading(false));
  }, [type, filterStartDate, filterEndDate, selectedCompany, title, highCostThreshold, modelStatus, numberOfFixesThreshold]);

  // Model link generator
  const getModelLink = useCallback((id, model_type) => {
    if (model_type === 'Space Model Variation' || model_type === 'Space Base Model') {
      return `/admin_rooms/${id}`;
    }
    return `/admin_products/${id}`;
  }, []);

  // Table columns
  const modelColumns = [
    {
      title: "Model ID",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => (parseFloat(a.id) || 0) - (parseFloat(b.id) || 0),
      render: (text, record) => (
        <a className="onhover-underline" href={getModelLink(text, record.model_type)}>
          {text}
        </a>
      ),
    },
    { title: "Model Name", dataIndex: "name", key: "name", ...getColumnSearchProps('name', 'Model Name') },
    { title: "Username", dataIndex: "customer_username", key: "customer_username", ...getColumnSearchProps('customer_username', 'Username') },
    { title: "Company", dataIndex: "company", key: "company", ...getColumnSearchProps('company', 'Company') },
    { title: "Category", dataIndex: "category", key: "category", ...getColumnSearchProps('category', 'Category') },
    { 
      title: "Artist Cost", 
      dataIndex: "artist_modeling_cost", 
      key: "artist_modeling_cost",
      sorter: (a, b) => (parseFloat(a.artist_modeling_cost) || 0) - (parseFloat(b.artist_modeling_cost) || 0),
      render: (text) => `$${text}`,
    },
    { 
      title: "Automation Cost", 
      dataIndex: "automation_cost", 
      key: "automation_cost",
      sorter: (a, b) => (parseFloat(a.automation_cost) || 0) - (parseFloat(b.automation_cost) || 0),
      render: (text) => `$${text}`,
    },
    { 
      title: "Total Cost", 
      dataIndex: "total_cost", 
      key: "total_cost",
      sorter: (a, b) => (parseFloat(a.total_cost) || 0) - (parseFloat(b.total_cost) || 0),
      render: (text) => `$${text}`,
    },
  ];

  // Table summary row
  const getTableSummary = useCallback(() => {
    if (!models?.length) return null;
    
    const totals = models.reduce((acc, record) => ({
      artistCost: acc.artistCost + (parseFloat(record.artist_modeling_cost) || 0),
      automationCost: acc.automationCost + (parseFloat(record.automation_cost) || 0),
      totalCost: acc.totalCost + (parseFloat(record.total_cost) || 0),
      count: acc.count + 1
    }), { artistCost: 0, automationCost: 0, totalCost: 0, count: 0 });

    const averages = {
      artistCost: totals.count > 0 ? totals.artistCost / totals.count : 0,
      automationCost: totals.count > 0 ? totals.automationCost / totals.count : 0,
      totalCost: totals.count > 0 ? totals.totalCost / totals.count : 0
    };

    return (
      <Table.Summary.Row style={{ backgroundColor: 'transparent', fontWeight: 'bold' }}>
        <Table.Summary.Cell index={0} colSpan={5}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center'
          }}>
            Averages
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5}>
          <span>${averages.artistCost.toFixed(2)}</span>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={6}>
          <span>${averages.automationCost.toFixed(2)}</span>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={7}>
          <span>${averages.totalCost.toFixed(2)}</span>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  }, [models]);

  // Initialize from URL parameters
  useEffect(() => {
    const urlParams = parseURLParams();
    
    setSelectedCompany(urlParams.selectedCompany);
    setHighCostThreshold(urlParams.highCostThreshold);
    setCurrentPage(urlParams.currentPage);
    setPageSize(urlParams.pageSize);
    // Don't set activeThreshold here - let the debounce effect handle it
    
    if (urlParams.selectedDateRange === 'custom' && urlParams.customStartDate && urlParams.customEndDate) {
      setCustomStartDate(urlParams.customStartDate);
      setCustomEndDate(urlParams.customEndDate);
      setFilterStartDate(urlParams.customStartDate.format(DATE_FORMAT));
      setFilterEndDate(urlParams.customEndDate.format(DATE_FORMAT));
      setSelectedDateRange('custom');
    } else {
      setSelectedDateRange(urlParams.selectedDateRange);
      
      const preset = DATE_PRESETS[urlParams.selectedDateRange];
      if (preset) {
        const { start, end } = preset.getDates();
        setFilterStartDate(start);
        setFilterEndDate(end);
      } else {
        const { start, end } = DATE_PRESETS.current_month.getDates();
        setFilterStartDate(start);
        setFilterEndDate(end);
        setSelectedDateRange('current_month');
      }
    }
    
    setIsInitialized(true);
  }, [parseURLParams]);

  // Handle prop changes (only when no URL params exist)
  useEffect(() => {
    if (!isInitialized && startDate && endDate) {
      const searchParams = new URLSearchParams(location.search);
      const prefix = getComponentPrefix();
      const hasURLParams = searchParams.has(`${prefix}_dr`) || 
                          searchParams.has(`${prefix}_cp`) || 
                          searchParams.has(`${prefix}_t`) ||
                          searchParams.has(`${prefix}_sd`) || 
                          searchParams.has(`${prefix}_ed`);
      
      if (!hasURLParams) {
        const { start, end } = DATE_PRESETS.current_month.getDates();
        setFilterStartDate(start);
        setFilterEndDate(end);
        setSelectedDateRange('current_month');
        setCustomStartDate(null);
        setCustomEndDate(null);
        setCurrentPage(1);
      }
    }
  }, [startDate, endDate, isInitialized, location.search]);

  // Debounced threshold effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (type === "high_cost") {
        updateURL({ highCostThreshold });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [highCostThreshold, type, updateURL]);

  // Data fetching effect
  useEffect(() => {
    if (isInitialized && filterStartDate && filterEndDate) {
      getModels();
    }
  }, [isInitialized, filterStartDate, filterEndDate, selectedCompany, highCostThreshold, getModels, modelStatus, numberOfFixesThreshold]);

  // Auto-expand collapsible when filters are applied
  useEffect(() => {
    if (isInitialized) {
      const filtersApplied = areFiltersApplied();
      if (filtersApplied) {
        setActiveCollapseKeys(['1']); // Open the panel
      } else {
        setActiveCollapseKeys([]); // Close the panel
      }
    }
  }, [isInitialized, areFiltersApplied]);


  return (
    <div className="high-cost-models-warning">
      <Card
        className="no-hover section-container"
        size="small"
        style={{ 
          marginBottom: 20, 
          border: `2px solid ${borderColor}`,
          boxShadow: `0 4px 12px ${shadowColor}`,
          background: backgroundColor
        }}
      >
        <Collapse 
          activeKey={activeCollapseKeys}
          onChange={setActiveCollapseKeys}
          ghost 
          expandIconPosition={'right'}
          style={{ background: 'transparent' }}
        >
          <Panel
            header={
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                color: borderColor,
                fontSize: '16px',
                width: '100%'
              }}>
                {icon}
                {title} {isLoading ? <LoadingOutlined style={{ marginLeft: '3px' }} /> : `(${models.length})`}
              </div>
            }
            key="1"
            style={{ border: 'none', background: 'transparent' }}
          >
            <div className="chart-container" style={{ paddingTop: '0px' }}>
              {/* Filters Section */}
              <div className="date-filter-section">
                <Row gutter={16} align="middle" justify="end">
                  <Col>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={handleExport}
                      disabled={!models?.length}
                      style={{ marginRight: 8 }}
                    >
                      Export CSV
                    </Button>
                  </Col>
                  
                  {type === "high_cost" && (
                    <Col>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: 8, fontSize: '14px', color: '#666' }}>
                          Number of Fixes Threshold:
                        </span>
                        <InputNumber
                          value={numberOfFixesThreshold}
                          onChange={handleNumberOfFixesThresholdChange}
                          min={0}
                          max={9999}
                          disabled={isLoading}
                          style={{ width: 120, height: '32px' }}
                          size="default"
                          step={1}
                          stringMode
                          controls={true}
                          onKeyPress={e => {
                            // Prevent non-numeric and negative sign input
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                        />
                      </div>
                    </Col>
                  )}


                  {type === "high_cost" && (
                    <Col>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: 8, fontSize: '14px', color: '#666' }}>
                          High Cost Threshold:
                        </span>
                        <InputNumber
                          value={highCostThreshold}
                          onChange={handleHighCostThresholdChange}
                          min={1}
                          max={9999}
                          disabled={isLoading}
                          formatter={value => {
                            // Always show $ prefix, show empty if no valid number and commas
                            const num = Number(value);
                            if (!num || num < 1) return '$';
                            return `$${num}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                          }}
                          parser={value => {
                            // Remove $ and commas, return null for invalid inputs
                            const parsed = value.replace(/\$\s?|(,*)/g, '');
                            const num = Number(parsed);
                            if (isNaN(num) || num < 1) return null;
                            return num;
                          }}
                          style={{ width: 120, height: '32px' }}
                          size="default"
                          step={1}
                          stringMode
                          controls={true}
                          onKeyPress={e => {
                            // Prevent non-numeric and negative sign input
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                        />
                      </div>
                    </Col>
                  )}
                  
                  <Col>
                    <Select
                      value={selectedCompany}
                      onChange={handleCompanyChange}
                      className="company-filter-select analytics-filter-centered-search-select"
                      style={{ minWidth: 200 }}
                      options={companiesList}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Col>
                  
                  <Col>
                    <Select
                      value={selectedDateRange}
                      onChange={handleDateRangeChange}
                      className="date-range-select"
                      options={dateOptions}
                    />
                  </Col>
                  
                  {selectedDateRange === 'custom' && (
                    <Col>
                      <RangePicker
                        value={customStartDate && customEndDate ? [customStartDate, customEndDate] : null}
                        onChange={handleCustomDateChange}
                        format={DATE_FORMAT}
                        placeholder={['Start Date', 'End Date']}
                        className="custom-date-picker"
                      />
                    </Col>
                  )}
                </Row>
              </div>
              
                             {/* Data Table */}
               {isLoading ? (
                 <div className="loading-container">
                   <LoadingOutlined style={{ fontSize: '24px', marginRight: '8px' }} />
                   Loading data...
                 </div>
               ) : error ? (
                 <Alert
                   message="Error Loading Data"
                   description="There was an error fetching the data. Please try again."
                   type="error"
                   showIcon
                   style={{ marginBottom: 16 }}
                 />
               ) : !hasModels ? (
                 <div className="empty-state-container">
                   <Empty
                     image={Empty.PRESENTED_IMAGE_SIMPLE}
                     description="No data found for the selected date range"
                     style={{ margin: '40px 0' }}
                   />
                 </div>
               ) : (
                 <Table
                   dataSource={models}
                   columns={modelColumns}
                   scroll={{ x: 1200 }}
                   bordered={true}
                   style={{ overflowX: "scroll" }}
                   pagination={{
                     current: currentPage,
                     pageSize: pageSize,
                     total: models.length,
                     showSizeChanger: false,
                     showQuickJumper: false,
                     showTotal: false,
                     position: ['bottomRight'],
                     size: 'default'
                   }}
                   onChange={handleTableChange}
                   size="small"
                   summary={getTableSummary}
                 />
               )}
            </div>
          </Panel>
        </Collapse>
      </Card>
    </div>
  );
};

export default withRouter(ModelsWarning);
